import { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '../stores/authStore';
import { apiFetch } from '../services/api';
import {
  LayoutDashboard, FileText, CheckSquare, Wallet, LogOut,
  Settings, Search, X,
} from 'lucide-react';

type TabId = 'dashboard' | 'notes' | 'tasks' | 'budget';

interface SidebarProps {
  activeTab: TabId;
  onTabChange: (tab: TabId) => void;
  onOpenSettings: () => void;
  onOpenSearch: () => void;
  isOpen: boolean;
  onClose: () => void;
}

const tabs: { id: TabId; label: string; icon: typeof FileText }[] = [
  { id: 'dashboard', label: 'Главная', icon: LayoutDashboard },
  { id: 'notes',  label: 'Заметки', icon: FileText },
  { id: 'tasks',  label: 'Задачи',  icon: CheckSquare },
  { id: 'budget', label: 'Бюджет',  icon: Wallet },
];

export function Sidebar({ activeTab, onTabChange, onOpenSettings, onOpenSearch, isOpen, onClose }: SidebarProps) {
  const { user, logout } = useAuthStore();
  const [taskBadge, setTaskBadge] = useState(0);

  const loadBadge = useCallback(async () => {
    try {
      const res = await apiFetch('/api/tasks/count');
      const data = await res.json();
      if (data.success) {
        setTaskBadge(data.data.count);
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadBadge(); const iv = setInterval(loadBadge, 30000); return () => clearInterval(iv); }, [loadBadge]);

  return (
    <>
      {/* Mobile backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-black/60 backdrop-blur-sm md:hidden transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
      />
      <aside className={`
        ${isOpen ? 'flex' : 'hidden'} md:flex flex-col
        fixed md:static inset-y-0 left-0 z-50 md:z-auto
        w-72 md:w-56 h-screen shrink-0
        bg-slate-900 border-r border-slate-700
      `}>
      {/* Logo */}
      <div className="px-4 py-5 border-b border-slate-700 flex items-center justify-between">
        <a href="https://witelab.ru" className="text-lg font-bold text-white hover:text-indigo-400 transition-colors">
          Daily Lab
        </a>
        <button onClick={onClose} className="md:hidden p-1.5 text-slate-500 hover:text-white rounded-lg hover:bg-slate-800 transition-colors">
          <X size={18} />
        </button>
      </div>

      {/* Tabs */}
      <nav className="flex-1 py-4 px-3 space-y-1.5">
        {tabs.map((t) => {
          const Icon = t.icon;
          const active = activeTab === t.id;
          const badge = t.id === 'tasks' && taskBadge > 0 ? taskBadge : null;
          return (
            <button
              key={t.id}
              onClick={() => { onTabChange(t.id); onClose(); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-base font-medium transition-all ${
                active
                  ? 'bg-gradient-to-r from-indigo-600 to-indigo-500 text-white shadow-lg shadow-indigo-500/20'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800/80'
              }`}
            >
              <Icon size={22} />
              {t.label}
              {badge && (
                <span className={`ml-auto text-[10px] px-2 py-0.5 rounded-full font-bold ${
                  active ? 'bg-white/20 text-white' : 'bg-indigo-600/30 text-indigo-300'
                }`}>
                  {badge}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Settings + User */}
      <div className="px-4 py-4 border-t border-slate-700">
        <button
          onClick={onOpenSearch}
          className="w-full flex items-center gap-2.5 px-4 py-3 mb-1 rounded-xl text-sm font-medium text-slate-400 hover:text-white hover:bg-slate-800/80 transition-all"
        >
          <Search size={20} />
          <span className="flex-1 text-left">Поиск</span>
          <kbd className="text-[10px] bg-slate-800 border border-slate-700 rounded px-1 py-0.5 text-slate-500">Ctrl+K</kbd>
        </button>
        <button
          onClick={onOpenSettings}
          className="w-full flex items-center gap-2.5 px-4 py-3 mb-3 rounded-xl text-sm font-medium text-slate-400 hover:text-white hover:bg-slate-800/80 transition-all"
        >
          <Settings size={20} />
          Настройки
        </button>

        {/* User + Logout */}
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-400 truncate">{user?.username}</span>
          <button
            onClick={logout}
            className="text-slate-500 hover:text-red-400 transition-colors"
            title="Выйти"
          >
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </aside>
    </>
  );
}
